// Review comments sidebar for a proposal — shown in the review (diff) view so
// reviewers can discuss a change before it's accepted. Lists general threads
// and node-anchored threads (with a node chip), carries the proposal reaction
// bar, and toggles "pin mode" for creating node-anchored comments on the board.
import { FormEvent, useState } from "react";
import { useDiffLookup } from "@archlang/app-shell/board";
import { Button } from "../ui/Button.js";
import { useContextMenu } from "../ui/ContextMenu";
import { Spinner } from "../ui/Spinner.js";
import { CommentThread } from "./CommentThread.js";
import { ReactionBar } from "./ReactionBar.js";
import { useReviewThreads } from "./useReviewThreads.js";
import { nodeCommentOutdated, shortNodeLabel } from "./nodeAnchors.js";
import { useTranslation } from "../i18n";

export function CommentsPanel({
  slug,
  repo,
  changesetId,
  pinMode = false,
  onTogglePinMode,
}: {
  readonly slug: string;
  readonly repo: string;
  readonly changesetId: string;
  readonly pinMode?: boolean;
  readonly onTogglePinMode?: () => void;
}) {
  const { t } = useTranslation("proposals");
  const review = useReviewThreads(slug, repo, changesetId);
  const lookup = useDiffLookup();
  const chipMenu = useContextMenu();
  const [draft, setDraft] = useState("");

  const roots = review.threads?.comments ?? [];
  const total = roots.length;

  function submit(e: FormEvent) {
    e.preventDefault();
    const body = draft.trim();
    if (!body) return;
    review.postComment(body);
    setDraft("");
  }

  return (
    <aside className="flex w-80 shrink-0 flex-col border-l border-slate-800 bg-slate-900">
      <header className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
        <span className="text-sm font-semibold text-slate-200">
          {t("panel.review")}
          {total > 0 && <span className="ml-1 text-slate-500">{t("panel.countParen", { count: total })}</span>}
        </span>
        {onTogglePinMode && (
          <button
            type="button"
            aria-pressed={pinMode}
            onClick={onTogglePinMode}
            className={`rounded-md border px-2 py-1 text-xs ${
              pinMode
                ? "border-sky-500/50 bg-sky-500/15 text-sky-200"
                : "border-slate-700 text-slate-400 hover:text-slate-200"
            }`}
          >
            {pinMode ? t("panel.pinActive") : t("panel.pin")}
          </button>
        )}
      </header>

      <div className="border-b border-slate-800 px-4 py-2">
        <ReactionBar
          reactions={review.threads?.reactions ?? []}
          onToggle={(e) => review.reactProposal(e)}
        />
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        {review.isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : review.isError ? (
          <p className="text-xs text-red-300">
            {t("panel.loadFailed", { message: review.error?.message ?? "" })}
          </p>
        ) : total === 0 ? (
          <p className="text-xs text-slate-500">{t("panel.empty")}</p>
        ) : (
          roots.map((c) => (
            <div key={c.id}>
              {c.anchorKind === "node" && c.nodeId && (
                <div className="mb-1 flex items-center gap-1 text-[11px] text-slate-500">
                  <span
                    className="rounded-sm bg-slate-800 px-1.5 py-0.5 font-mono text-slate-300"
                    onContextMenu={(e) =>
                      chipMenu.open(e, [
                        {
                          label: t("panel.copyNodeId"),
                          onClick: () => void navigator.clipboard?.writeText(c.nodeId ?? ""),
                        },
                      ])
                    }
                  >
                    {shortNodeLabel(c.nodeKind, c.nodeId)}
                  </span>
                </div>
              )}
              <CommentThread
                comment={c}
                myId={review.myId}
                busy={review.busy}
                outdated={nodeCommentOutdated(lookup, c)}
                handlers={review.handlers}
              />
            </div>
          ))
        )}
      </div>

      <form className="border-t border-slate-800 p-3" onSubmit={submit}>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={3}
          placeholder={t("panel.composerPlaceholder")}
          aria-label={t("panel.composerAria")}
          className="w-full resize-none rounded-md border border-slate-700 bg-slate-950 p-2 text-sm text-slate-200 placeholder:text-slate-600 focus:border-slate-500 focus:outline-none"
        />
        <div className="mt-2 flex justify-end">
          <Button type="submit" variant="primary" size="sm" disabled={!draft.trim() || review.busy}>
            {t("panel.submit")}
          </Button>
        </div>
      </form>
      {chipMenu.node}
    </aside>
  );
}
